/**
 * useModerationNotice — one hook for showing the branded `ModerationWarning`
 * banner across every pre-publish form outside chats (profile, master bio,
 * lives, video practices, library).
 *
 * Replaces the scattered `ensureContentIsSafe` → `window.alert` pattern. Two
 * entry points, one banner:
 *   - `guard(text)`        — проверка перед отправкой. `true` — можно
 *                            отправлять, `false` — показан баннер. CORE-196:
 *                            если проверить не удалось, тоже `false` — но с
 *                            текстом «попробуйте ещё раз», а не с обвинением.
 *   - `flagFromError(err)` — 422 от бэкенда в тот же баннер. Возвращает `true`,
 *                            если отказ был от модерации, чтобы вызывающий не
 *                            показывал сверху свою общую ошибку сохранения.
 *
 * Написанное человеком форма НЕ очищает: отказ — повод поправить текст или
 * повторить попытку, а не набирать всё заново.
 *
 * Usage:
 *   const moderation = useModerationNotice();
 *   // in the submit handler:
 *   if (!(await moderation.guard(text))) return;
 *   try { await api.save(...); }
 *   catch (err) { if (!moderation.flagFromError(err)) setSaveError(friendlyApiError(err)); }
 *   // in JSX, near the Save button:
 *   {moderation.banner}
 */

import React, { useCallback, useState } from 'react';
import {
  checkContentModeration,
  isUnavailableResult,
  ModerationViolation,
} from '../services/moderation';
import {
  isModerationBlock,
  isModerationUnavailable,
  MODERATION_UNAVAILABLE_MESSAGE,
} from '../services/errorMessages';
import { ModerationWarning } from '../components/ModerationWarning';

interface Notice {
  violations?: ModerationViolation[];
  message?: string;
}

export function useModerationNotice() {
  const [notice, setNotice] = useState<Notice | null>(null);

  const clear = useCallback(() => setNotice(null), []);

  const guard = useCallback(
    async (text?: string | null, imageUrl?: string): Promise<boolean> => {
      const trimmed = (text ?? '').trim();
      if (!trimmed && !imageUrl) {
        setNotice(null);
        return true;
      }
      const res = await checkContentModeration({
        text: trimmed || undefined,
        imageUrl,
      });
      if (res.flagged) {
        // Сбой проверки — не вина автора: показываем «попробуйте ещё раз»
        // вместо списка нарушенных категорий, которых на самом деле нет.
        setNotice(
          isUnavailableResult(res)
            ? { message: MODERATION_UNAVAILABLE_MESSAGE }
            : { violations: res.violations },
        );
        return false;
      }
      setNotice(null);
      return true;
    },
    [],
  );

  const flagFromError = useCallback((err: unknown): boolean => {
    if (!isModerationBlock(err)) return false;
    // Бэкенд не присылает категории в 422, поэтому показываем общий текст —
    // но разный: «не удалось проверить» и «нарушает правила» это разные
    // сообщения, и путать их значит обвинять человека за наш сбой.
    setNotice(
      isModerationUnavailable(err)
        ? { message: MODERATION_UNAVAILABLE_MESSAGE }
        : {},
    );
    return true;
  }, []);

  const banner = notice ? (
    <ModerationWarning
      violations={notice.violations}
      message={notice.message}
      onDismiss={clear}
    />
  ) : null;

  return { guard, flagFromError, clear, banner, blocked: notice != null };
}
